import { PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { selectIsAuthenticated } from '@paradigm/store/auth';

import { useTerminalDrawerState } from '#/features/terminal/drawer-context';
import { UnifiedRfqProviders } from '#/components/UnifiedRfqs/UnifiedRfqs';
import { UNIFIED_RFQS_ROUTE } from '#/routes';

/**
 * Mounts `UnifiedRfqProviders` once, above both the route tree and the
 * Block Terminal side panel, but only when something downstream actually
 * needs it:
 *
 *   - the user is authenticated, AND
 *   - the current route is `/rfqs` (the page consumes the providers), OR
 *     the Block Terminal side panel is open in side-panel mode.
 *
 * Why authentication-gated? `UnifiedRfqProviders` requires
 * `AccountsProvider` upstream, and `LayoutContainer` only mounts that
 * on its authenticated branch. Without this gate, logging out while
 * the side panel was open (the drawer's `isOpen`/`pinMode` lives in
 * localStorage and persists across logout) would crash the app with
 * "Missing AccountsView context provider", with no recovery on reload.
 *
 * Why not mount it inside the `/rfqs` page (as we used to)? The side
 * panel is rendered as a sibling of `<Routes />` in `App.tsx`, not as a
 * descendant, so a page-level wrap can't be shared with it via React
 * context — the panel would mount its own providers, spawning a second
 * `DrfqNotificationsService` and firing every fill toast twice.
 *
 * Why not mount it unconditionally on every authenticated route? Then
 * any user on `/paradex` or `/futurespreads` would receive RFQ/OB
 * notifications they never opted into.
 */
export default function ScopedUnifiedRfqProviders({
  children,
}: PropsWithChildren) {
  const { pathname } = useLocation();
  const { isOpen, pinMode } = useTerminalDrawerState();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const isOnRfqsRoute = pathname === UNIFIED_RFQS_ROUTE;
  const sidePanelActive = isOpen && pinMode === 'side-panel';
  const needsProviders = isAuthenticated && (isOnRfqsRoute || sidePanelActive);

  // Fragment is needed so the function returns a JSX element rather than
  // raw ReactNode (which is not a valid component return type under
  // strict TS settings).
  if (!needsProviders) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }
  return <UnifiedRfqProviders>{children}</UnifiedRfqProviders>;
}
